import { ref } from 'vue';
import type { RouteLocationRaw, Router } from 'vue-router';
import { AREAS, areaDestinations, areaForEntity, listPathFor, type Area, type AreaId } from '@/navigation/areas';
import { areaLanding, isEntityListRoute, type RouteLike } from '@/navigation/activeArea';

/**
 * Each area remembers the last list or page you were on in it, so going
 * Music → Custom playlists → Home → Music lands back on Custom playlists, with
 * the saved view it had open. The icon strip, the Settings gear, the phone's
 * bottom bar and Cmd+K's area commands all go there.
 *
 * The spots live in sessionStorage only, so each tab keeps its own and they
 * survive a reload, including the reload after a deploy. Nothing is copied
 * into memory: every link reads storage again and checks the spot against the
 * user's gates. So a spot the user can no longer open, another user's spots,
 * and storage that is blocked or full all fall back to the area's first list,
 * as if the area had never been visited.
 */

const STORAGE_KEY = 'tabasco:area-spots';

interface StoredSpots {
    userId: number;
    spots: Partial<Record<AreaId, unknown>>;
}

// Goes up on every write. Storage is not reactive, so a link reads this first
// to redraw when the spot changes, as the lit icon must while you move between
// lists inside its area.
const spotsVersion = ref(0);

/**
 * The area a route is a spot of, and the spot: the list's or page's own address
 * plus the route's query. Null for a record, a page's detail address such as
 * `/audio-file-issues/E1`, Home, and anything outside the areas.
 *
 * The address comes from the area table, never the route's path, so a visit to
 * `/music/custom-playlists/` is stored as `/music/custom-playlists`.
 */
export function spotForRoute(route: RouteLike & { params?: Record<string, unknown>; fullPath: string }): { areaId: AreaId; spot: string } | null {
    // The hash comes after the query and may itself hold a `?`, so cut it off first.
    const withoutHash = route.fullPath.split('#')[0];
    const queryStart = withoutHash.indexOf('?');
    const query = queryStart === -1 ? '' : withoutHash.slice(queryStart);

    if (isEntityListRoute(route)) {
        const entityId = route.meta.entityId as string;
        const area = areaForEntity(entityId);
        return area ? { areaId: area.id, spot: listPathFor(entityId) + query } : null;
    }
    for (const area of AREAS) {
        const page = area.pages.find((candidate) => candidate.routeName === route.name);
        if (page) {
            return { areaId: area.id, spot: page.path + query };
        }
    }
    return null;
}

function readStored(userId: number): StoredSpots['spots'] {
    try {
        const stored: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
        if (typeof stored !== 'object' || stored === null) return {};
        const { userId: storedUserId, spots } = stored as Partial<StoredSpots>;
        return storedUserId === userId && typeof spots === 'object' && spots !== null ? spots : {};
    } catch {
        return {};
    }
}

/** Remembers the route as its area's spot, for the signed-in user. Does nothing for a route that is no area's spot. */
export function recordAreaSpot(route: Parameters<typeof spotForRoute>[0], userId: number | undefined): void {
    const found = spotForRoute(route);
    if (!found || userId === undefined) return;
    const spots = { ...readStored(userId), [found.areaId]: found.spot };
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, spots } satisfies StoredSpots));
    } catch {
        return;
    }
    spotsVersion.value += 1;
}

/**
 * The area's remembered spot, or null when there is none it can use. The spot
 * must still be one of the area's lists or pages, so pass an area from
 * `visibleAreas`: then a spot whose gate has since closed is refused too.
 */
export function rememberedSpot(area: Area, userId: number | undefined): string | null {
    void spotsVersion.value;
    if (userId === undefined) return null;
    const spot = readStored(userId)[area.id];
    if (typeof spot !== 'string') return null;
    const path = spot.split('?')[0];
    return areaDestinations(area).some((destination) => destination.path === path) ? spot : null;
}

/** Where opening an area goes: its remembered spot, else its first list. Pass an area from `visibleAreas`. */
export function areaEntry(area: Area, userId: number | undefined): RouteLocationRaw {
    return rememberedSpot(area, userId) ?? areaLanding(area);
}

/** Records each finished navigation as its area's spot. A cancelled or failed navigation records nothing. */
export function installAreaSpots(router: Pick<Router, 'afterEach'>, currentUserId: () => number | undefined): void {
    router.afterEach((to, _from, failure) => {
        if (!failure) recordAreaSpot(to, currentUserId());
    });
}
